"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.assignMailSchema = exports.INBOX_BODY_SHOWN_MAX = exports.unassignedMailQuerySchema = exports.INBOX_PAGE_SIZE = exports.INBOUND_MATCHES = exports.peopleSearchQuerySchema = exports.PERSON_APPLICATIONS_LIMIT = exports.PEOPLE_SEARCH_LIMIT = exports.PEOPLE_SEARCH_MIN = exports.templatePreviewSchema = exports.TEMPLATE_AUDIENCE_LABELS = exports.TEMPLATE_AUDIENCES = exports.createTemplateSchema = exports.saveTemplateSchema = exports.TEMPLATE_KINDS = exports.composeMessageSchema = exports.MESSAGE_BODY_MAX = exports.MESSAGE_SUBJECT_MAX = exports.VARIABLE_LABELS = exports.MESSAGE_VARIABLE_LABELS = exports.MESSAGE_VARIABLES = exports.TASK_LIST_SCOPES = exports.askHrSchema = exports.taskVersionSchema = exports.reassignTaskSchema = exports.completeTaskSchema = exports.createTaskSchema = exports.TASK_KINDS = exports.timelineQuerySchema = exports.TIMELINE_FILTER_LABELS = exports.TIMELINE_FILTERS = exports.logActivitySchema = exports.NO_FOLLOW_UP_MESSAGE = exports.TASK_OVERDUE_AFTER_HOURS = exports.TASK_DUE_HOUR = exports.ACTIVITY_DIRECTIONS = exports.CALL_OUTCOME_LABELS = exports.CALL_OUTCOMES = exports.ACTIVITY_TYPE_LABELS = exports.LOGGABLE_ACTIVITY_TYPES = void 0;
exports.isContactAttempt = isContactAttempt;
exports.compareTasks = compareTasks;
exports.variablesIn = variablesIn;
exports.unknownVariables = unknownVariables;
exports.fillVariables = fillVariables;
exports.strayBraces = strayBraces;
exports.variableLabel = variableLabel;
exports.toLabelledText = toLabelledText;
exports.fromLabelledText = fromLabelledText;
exports.labelledTokens = labelledTokens;
exports.customTemplateKey = customTemplateKey;
exports.templateAudienceOf = templateAudienceOf;
const zod_1 = require("zod");
const calendars_1 = require("./calendars");
const schemas_1 = require("./schemas");
// S26 — the activity log, tasks, messages and inbound email: COMM-01–04, ADMIN-09; F15.01–F15.08.
// Decisions: DECISIONS-LOG S25–S28 Q7–Q12 (taken as recommended, by instruction).
// --- Activities (F15.01, COMM-02) ---------------------------------------------------------------
/** What a person logs by hand. `email_in` and `system` are written by the system, never typed. */
exports.LOGGABLE_ACTIVITY_TYPES = ['call', 'email_out', 'sms', 'note', 'meeting'];
exports.ACTIVITY_TYPE_LABELS = {
    call: 'Call',
    email_out: 'Email',
    sms: 'SMS',
    note: 'Note',
    meeting: 'Meeting',
    email_in: 'Email received',
    system: 'System',
};
/** E13's four call outcomes. */
exports.CALL_OUTCOMES = ['reached', 'no_answer', 'rescheduled', 'declined'];
exports.CALL_OUTCOME_LABELS = {
    reached: 'Reached',
    no_answer: 'No answer',
    rescheduled: 'Rescheduled',
    declined: 'Declined',
};
exports.ACTIVITY_DIRECTIONS = ['outbound', 'inbound'];
/**
 * §L19 GEN-UNRESPONSIVE needs three logged contact attempts. Q11: any call, SMS or email we
 * logged going out counts — except a call marked "reached", which is contact, not an attempt.
 */
function isContactAttempt(type, direction, outcome) {
    if (direction === 'inbound')
        return false;
    if (type === 'call')
        return outcome !== 'reached';
    return type === 'sms' || type === 'email_out';
}
/** Tasks fall due at the start of the working day, Dhaka time (D14), on the day chosen. */
exports.TASK_DUE_HOUR = 9;
/** §L22 N-63: "+24h" after the task fell due. */
exports.TASK_OVERDUE_AFTER_HOURS = 24;
exports.NO_FOLLOW_UP_MESSAGE = 'No follow-up? Add a next action, or tick "No follow-up needed".';
const nextActionSchema = zod_1.z.object({
    title: zod_1.z.string().trim().min(2, 'Say what happens next.').max(200, 'Keep it under 200 characters.'),
    dueOn: calendars_1.isoDateSchema,
    /** Null = whoever logs it. */
    assigneeUserId: schemas_1.uuidSchema.nullable().default(null),
});
exports.logActivitySchema = zod_1.z
    .object({
    candidateId: schemas_1.uuidSchema.nullable().default(null),
    applicationId: schemas_1.uuidSchema.nullable().default(null),
    type: zod_1.z.enum(exports.LOGGABLE_ACTIVITY_TYPES),
    direction: zod_1.z.enum(exports.ACTIVITY_DIRECTIONS).default('outbound'),
    outcome: zod_1.z.enum(exports.CALL_OUTCOMES).nullable().default(null),
    body: zod_1.z.string().trim().max(4000, 'Keep it under 4,000 characters.').default(''),
    /** When it happened, if not just now (a call logged after lunch). */
    occurredAt: zod_1.z.iso.datetime().nullable().default(null),
    nextAction: nextActionSchema.nullable().default(null),
    /** E13: "the UI always asks 'No follow-up?'" — the answer, given on purpose. */
    noFollowUp: zod_1.z.boolean().default(false),
})
    .refine((value) => value.candidateId !== null || value.applicationId !== null, { path: ['candidateId'], message: 'Log it against a person or an application.' })
    .refine((value) => value.type === 'call' || value.outcome === null, { path: ['outcome'], message: 'Only a call has an outcome.' })
    .refine((value) => value.type !== 'note' || value.body !== '', { path: ['body'], message: 'Write the note.' })
    .refine((value) => value.nextAction !== null || value.noFollowUp, { path: ['nextAction'], message: exports.NO_FOLLOW_UP_MESSAGE });
// --- The timeline (COMM-01) --------------------------------------------------------------------
exports.TIMELINE_FILTERS = ['all', 'call', 'email', 'sms', 'note', 'meeting', 'system'];
exports.TIMELINE_FILTER_LABELS = {
    all: 'Everything',
    call: 'Calls',
    email: 'Emails',
    sms: 'SMS',
    note: 'Notes',
    meeting: 'Meetings',
    system: 'System',
};
exports.timelineQuerySchema = zod_1.z.object({
    type: zod_1.z.enum(exports.TIMELINE_FILTERS).default('all'),
    /** Older than this — the next page down. */
    before: zod_1.z.iso.datetime().optional(),
    limit: zod_1.z.coerce.number().int().positive().max(200).default(60),
});
// --- Tasks (F15.05–F15.07, COMM-04) ------------------------------------------------------------
exports.TASK_KINDS = ['follow_up', 'ask_hr', 'standalone'];
exports.createTaskSchema = zod_1.z.object({
    title: zod_1.z.string().trim().min(2, 'Say what needs doing.').max(200, 'Keep it under 200 characters.'),
    description: zod_1.z.string().trim().max(2000).nullable().default(null),
    dueOn: calendars_1.isoDateSchema.nullable().default(null),
    candidateId: schemas_1.uuidSchema.nullable().default(null),
    applicationId: schemas_1.uuidSchema.nullable().default(null),
    assigneeUserId: schemas_1.uuidSchema.nullable().default(null),
});
exports.completeTaskSchema = zod_1.z.object({
    version: zod_1.z.number().int().positive(),
    /** An "ask HR" task is answered as it is completed (Q10). */
    answer: zod_1.z.string().trim().max(2000).nullable().default(null),
});
exports.reassignTaskSchema = zod_1.z.object({
    version: zod_1.z.number().int().positive(),
    assigneeUserId: schemas_1.uuidSchema,
});
exports.taskVersionSchema = zod_1.z.object({ version: zod_1.z.number().int().positive() });
/** O15 / Q10: a hiring manager's question about one of their openings. */
exports.askHrSchema = zod_1.z.object({
    requisitionId: schemas_1.uuidSchema,
    question: zod_1.z.string().trim().min(5, 'Write the question.').max(2000, 'Keep it under 2,000 characters.'),
});
exports.TASK_LIST_SCOPES = ['open', 'done'];
/**
 * COMM-04's order: overdue first (oldest due first), then due soonest, then undated; finished
 * ones newest first. Pure, so the order is tested once and shared.
 */
function compareTasks(a, b) {
    const aDone = a.status === 'done' || a.status === 'cancelled';
    const bDone = b.status === 'done' || b.status === 'cancelled';
    if (aDone !== bDone)
        return aDone ? 1 : -1;
    if (aDone)
        return (b.completedAt ?? '').localeCompare(a.completedAt ?? '');
    if (a.dueAt === null && b.dueAt === null)
        return a.createdAt.localeCompare(b.createdAt);
    if (a.dueAt === null)
        return 1;
    if (b.dueAt === null)
        return -1;
    return a.dueAt.localeCompare(b.dueAt);
}
// --- Messages (F15.02, F15.03, COMM-03) --------------------------------------------------------
/**
 * The variable palette for a message to a candidate (E13: "{{first_name}}, {{job_title}},
 * {{interview_time}}"). Written camelCase like every template the system sends.
 */
exports.MESSAGE_VARIABLES = ['firstName', 'candidateName', 'jobTitle', 'companyName', 'recruiterName', 'interviewTime'];
exports.MESSAGE_VARIABLE_LABELS = {
    firstName: 'First name',
    candidateName: 'Full name',
    jobTitle: 'Job title',
    companyName: 'Company',
    recruiterName: 'Your name',
    interviewTime: 'Next interview',
};
/** `{{name}}`, as `TemplateService` substitutes. */
const VARIABLE = /\{\{\s*([A-Za-z][A-Za-z0-9_]*)\s*\}\}/g;
function variablesIn(text) {
    return [...new Set([...text.matchAll(VARIABLE)].map((match) => match[1] ?? ''))].filter((name) => name !== '');
}
/** Variables a text uses that are not on its palette — refused before anything is sent. */
function unknownVariables(text, allowed) {
    return variablesIn(text).filter((name) => !allowed.includes(name));
}
/** Fills what is known; anything without a value becomes "—" rather than "{{name}}". */
function fillVariables(text, values) {
    return text.replace(VARIABLE, (_whole, name) => values[name] ?? '—');
}
/** Anything in double braces, variable or not: `{{Button link}}`, `{{ }}`, `{{jobTitle}}`. */
const BRACED = /\{\{([^{}\n]*)\}\}/g;
/** Exactly one variable, as `variablesIn` reads it. */
const ONE_VARIABLE = /^\{\{\s*[A-Za-z][A-Za-z0-9_]*\s*\}\}$/;
/**
 * Double braces that are not a variable at all — `{{Button link}}` typed by hand, `{{ }}` — which no sender fills,
 * so the candidate would read the braces themselves. Refused on save beside the unknown names (ADMIN-09).
 */
function strayBraces(text) {
    return [...new Set([...text.matchAll(BRACED)].map((match) => match[0]).filter((token) => !ONE_VARIABLE.test(token)))];
}
/**
 * Every variable's name in plain words: the compose palette's, then the ones only the system's own emails use (Admin
 * redesign batch 2, Message templates Option A: "variables show as blue pills, so nobody types {{braces}} by hand").
 * Unique without regard to case, because an editor turns a label back into its variable that way.
 */
exports.VARIABLE_LABELS = {
    ...exports.MESSAGE_VARIABLE_LABELS,
    fullName: 'Their name',
    deepLinkUrl: 'Button link',
    subjectName: 'Who it is about',
    subjectEmail: 'Their email',
    summary: 'What changed',
    endsOn: 'Cover ends',
    title: 'Headline',
    body: 'Message',
    testName: 'Test name',
    minutes: 'Minutes',
    expiresAt: 'Link works until',
    when: 'Date and time',
    venue: 'Venue',
    place: 'Where',
    reason: 'Reason',
    details: 'Interview details',
    days: 'Days',
    stageName: 'Next round',
    validUntil: 'Offer open until',
    documentName: 'Document',
    sections: 'Sections',
    sectionsText: 'Sections as text',
    dictionary: 'Metric definitions',
    sentBy: 'Sent by',
    count: 'How many',
    plural: 'Plural ending',
    items: 'Items',
    itemsText: 'Items as text',
    unlocksAt: 'Unlocks at',
};
/** A variable's plain name, or its own name where it has none yet. */
function variableLabel(name) {
    return exports.VARIABLE_LABELS[name] ?? name;
}
/** The text a person edits: `{{jobTitle}}` written as `{{Job title}}`. A variable with no label stays as it is. */
function toLabelledText(text) {
    return text.replace(VARIABLE, (whole, name) => {
        const label = exports.VARIABLE_LABELS[name];
        return label ? `{{${label}}}` : whole;
    });
}
/**
 * Back to what is stored and sent: each `{{Label}}` of this palette becomes `{{name}}`, as does a `{{name}}` typed
 * by hand. Anything else is left exactly as typed, so the save refuses it with its own words rather than guessing.
 */
function fromLabelledText(text, palette) {
    return text.replace(BRACED, (whole, inside) => {
        const named = paletteNameOf(inside, palette);
        return named ? `{{${named}}}` : whole;
    });
}
/** The palette's variable a `{{…}}`'s inside names, by its label (any case, any spacing) or its own name. */
function paletteNameOf(inside, palette) {
    const words = inside.trim().replace(/\s+/g, ' ');
    if (palette.includes(words))
        return words;
    const lower = words.toLowerCase();
    return palette.find((name) => variableLabel(name).toLowerCase() === lower);
}
/** The pills of an edited text, in order, for the editor's highlight and its "can't fill" warning. */
function labelledTokens(text, palette) {
    return [...text.matchAll(BRACED)].map((match) => {
        const token = match[0];
        return { start: match.index, end: match.index + token.length, text: token, isKnown: paletteNameOf(match[1] ?? '', palette) !== undefined };
    });
}
exports.MESSAGE_SUBJECT_MAX = 200;
exports.MESSAGE_BODY_MAX = 10_000;
exports.composeMessageSchema = zod_1.z
    .object({
    applicationId: schemas_1.uuidSchema.nullable().default(null),
    candidateId: schemas_1.uuidSchema.nullable().default(null),
    /** The template it started from, for ADMIN-09's usage count. Free text when null (E13). */
    templateKey: zod_1.z.string().trim().max(120).nullable().default(null),
    subject: zod_1.z.string().trim().min(1, 'Write a subject.').max(exports.MESSAGE_SUBJECT_MAX),
    body: zod_1.z.string().trim().min(1, 'Write the message.').max(exports.MESSAGE_BODY_MAX),
})
    .refine((value) => value.applicationId !== null || value.candidateId !== null, { path: ['candidateId'], message: 'Choose who it goes to.' });
// --- Templates (F15.08, ADMIN-09) --------------------------------------------------------------
exports.TEMPLATE_KINDS = ['system', 'custom'];
exports.saveTemplateSchema = zod_1.z.object({
    name: zod_1.z.string().trim().min(2, 'Name the template.').max(120),
    description: zod_1.z.string().trim().max(400).nullable().default(null),
    subject: zod_1.z.string().trim().min(1, 'Write a subject.').max(exports.MESSAGE_SUBJECT_MAX),
    bodyText: zod_1.z.string().trim().min(1, 'Write the message.').max(exports.MESSAGE_BODY_MAX),
    version: zod_1.z.number().int().positive(),
});
exports.createTemplateSchema = exports.saveTemplateSchema.omit({ version: true });
/** Turns a template name into a key that cannot clash with the system's. */
function customTemplateKey(name, suffix) {
    const slug = name.toLowerCase().normalize('NFKD').replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 40);
    return `custom.${slug || 'template'}_${suffix}`;
}
/**
 * Who a system email goes to, so its 50-odd keys can be found (Admin redesign batch 2, Message templates). Sign-in and
 * account emails carry an account link; an email that names the candidate goes to them; the rest go to staff.
 */
exports.TEMPLATE_AUDIENCES = ['candidates', 'staff', 'account'];
exports.TEMPLATE_AUDIENCE_LABELS = {
    candidates: 'To candidates',
    staff: 'To staff',
    account: 'Sign-in and account',
};
const ACCOUNT_KEYS = ['user.invited', 'user.access_changed'];
function templateAudienceOf(template) {
    if (template.key.startsWith('auth.') || ACCOUNT_KEYS.includes(template.key))
        return 'account';
    return template.placeholders.includes('candidateName') ? 'candidates' : 'staff';
}
/**
 * ADMIN-09's preview (Admin redesign batch 2): the words being written, as the email they make, filled from a real
 * application when one is chosen. It reads only: nothing is sent, nothing is saved, no link is made.
 */
exports.templatePreviewSchema = zod_1.z.object({
    /** The template being edited, for its palette and its button; null for a new one of the team's. */
    templateId: schemas_1.uuidSchema.nullable().default(null),
    subject: zod_1.z.string().max(exports.MESSAGE_SUBJECT_MAX).default(''),
    bodyText: zod_1.z.string().max(exports.MESSAGE_BODY_MAX).default(''),
    applicationId: schemas_1.uuidSchema.nullable().default(null),
});
// --- Finding a person and their applications (Inbound mail's filing, the preview's "As …") ----------------------
exports.PEOPLE_SEARCH_MIN = 2;
exports.PEOPLE_SEARCH_LIMIT = 6;
/** Per person: their open applications first, then the newest closed ones. */
exports.PERSON_APPLICATIONS_LIMIT = 6;
exports.peopleSearchQuerySchema = zod_1.z.object({ q: zod_1.z.string().trim().max(200).default('') });
// --- Inbound email (F15.04) --------------------------------------------------------------------
exports.INBOUND_MATCHES = ['thread', 'sender', 'manual'];
/** Inbound mail pages through the queue (Admin redesign batch 2: "paging past 200"). */
exports.INBOX_PAGE_SIZE = 50;
exports.unassignedMailQuerySchema = zod_1.z.object({ page: zod_1.z.coerce.number().int().min(1).max(100_000).default(1) });
/** The longest body shown; the mailbox reader keeps no more than this either. */
exports.INBOX_BODY_SHOWN_MAX = 20_000;
exports.assignMailSchema = zod_1.z
    .object({
    candidateId: schemas_1.uuidSchema.nullable().default(null),
    applicationId: schemas_1.uuidSchema.nullable().default(null),
})
    .refine((value) => value.candidateId !== null || value.applicationId !== null, { path: ['candidateId'], message: 'Choose who it belongs to.' });
//# sourceMappingURL=communication.js.map